import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification // <--- Added for verification
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  onSnapshot, 
  deleteDoc, 
  doc, 
  query, 
  updateDoc
} from 'firebase/firestore';
import { 
  Plus, 
  Trash2, 
  X, 
  Edit2,
  LogIn,
  LogOut,
  User,
  Navigation,
  MapPin,
  Compass,
  AlertTriangle,
  Mail
} from 'lucide-react';

// --- 1. CONFIGURATION ---
const firebaseConfig = {
  apiKey: "AIzaSyDLxWvxOOApDv3NZUX97UldUGk-PIprniU",
  authDomain: "tilalta.firebaseapp.com",
  projectId: "tilalta",
  storageBucket: "tilalta.firebasestorage.app",
  messagingSenderId: "134708400014",
  appId: "1:134708400014:web:b14c1d8a10645df2779edf",
  measurementId: "G-0BGZ5BK4R1"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = "tilalta";

// --- UI COMPONENTS ---
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-soft border-2 border-brand-sand/30 overflow-hidden ${className}`}>
    {children}
  </div>
);

const Button = ({ onClick, children, variant = "primary", className = "", type = "button" }) => {
  const baseStyles = "px-5 py-2.5 rounded-lg font-bold transition-all duration-200 flex items-center justify-center gap-2 active:translate-y-0.5 active:shadow-none";
  const variants = {
    primary: "bg-brand-moss text-white hover:bg-[#3f4637] shadow-earthy border-b-4 border-[#3f4637]",
    accent: "bg-brand-brown text-white hover:bg-[#5e3413] shadow-earthy border-b-4 border-[#5e3413]",
    outline: "bg-transparent border-2 border-brand-moss text-brand-moss hover:bg-brand-moss/5",
    ghost: "bg-transparent text-brand-moss/70 hover:bg-brand-sand/20 hover:text-brand-brown",
    danger: "bg-red-50 text-red-600 hover:bg-red-100 border border-red-100"
  };
  return (
    <button type={type} onClick={onClick} className={`${baseStyles} ${variants[variant]} ${className}`}>
      {children}
    </button>
  );
};

const Badge = ({ children }) => (
  <span className="px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider bg-brand-sand/30 text-brand-brown border border-brand-sand/50">
    {children}
  </span>
);

// --- AUTH FORM (Updated with Verification) ---
const AuthForm = ({ onLoginSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
        onLoginSuccess();
      } else {
        // 1. Create User
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // 2. Send Verification Email
        await sendEmailVerification(userCredential.user);
        // 3. Show Success Message
        setVerificationSent(true);
      }
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') setError("Sähköposti on jo käytössä.");
      else if (err.code === 'auth/weak-password') setError("Salasana on liian lyhyt.");
      else setError("Virhe. Tarkista tiedot.");
    }
  };

  if (verificationSent) {
    return (
      <div className="p-8 bg-brand-cream text-center">
        <div className="inline-block p-3 bg-green-100 rounded-full mb-4">
           <Mail size={32} className="text-green-700" />
        </div>
        <h2 className="text-2xl font-bold text-brand-moss mb-2">Vahvistusviesti lähetetty!</h2>
        <p className="text-brand-moss/80 mb-6 text-sm">
          Lähetimme linkin osoitteeseen <strong>{email}</strong>.<br/>
          Klikkaa linkkiä sähköpostissa aktivoidaksesi tilin.
        </p>
        <Button onClick={onLoginSuccess} variant="outline" className="w-full">
          Selvä, sulje
        </Button>
      </div>
    );
  }

  return (
    <div className="p-8 bg-brand-cream">
      <div className="mb-6 text-center">
        <div className="inline-block p-3 bg-brand-sand/20 rounded-full mb-3">
           <Compass size={32} className="text-brand-brown" />
        </div>
        <h2 className="text-2xl font-bold text-brand-moss">{isLogin ? 'Tervetuloa' : 'Luo tunnus'}</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 text-red-800 text-sm border-l-4 border-red-500">
             {error}
          </div>
        )}
        
        <div>
          <label className="block text-xs font-bold text-brand-brown uppercase mb-1">Sähköposti</label>
          <input 
            type="email" required 
            className="w-full px-4 py-3 rounded-lg bg-white border-2 border-brand-sand/50 focus:border-brand-brown outline-none transition-colors text-brand-moss"
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-brand-brown uppercase mb-1">Salasana</label>
          <input 
            type="password" required 
            className="w-full px-4 py-3 rounded-lg bg-white border-2 border-brand-sand/50 focus:border-brand-brown outline-none transition-colors text-brand-moss"
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="pt-2">
          <Button type="submit" variant="accent" className="w-full">
            {isLogin ? 'Kirjaudu' : 'Rekisteröidy'}
          </Button>
        </div>

        <div className="text-center pt-2">
          <button type="button" onClick={() => setIsLogin(!isLogin)}
            className="text-sm text-brand-moss hover:text-brand-brown underline decoration-brand-sand underline-offset-4">
            {isLogin ? "Puuttuuko tunnus? Rekisteröidy" : "Onko sinulla jo tunnus? Kirjaudu"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default function App() {
  const [isReady, setIsReady] = useState(false);
  const [user, setUser] = useState(null);
  const [pins, setPins] = useState([]);
  
  // Refs for Map (fixes white screen bug)
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  
  const [isAddingMode, setIsAddingMode] = useState(false);
  const [newPinCoords, setNewPinCoords] = useState(null);
  const [selectedPin, setSelectedPin] = useState(null);
  const [isPinFormOpen, setIsPinFormOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ title: '', intro: '' });

  // Safety Check
  useEffect(() => {
    const checkScriptLoad = setInterval(() => {
      if (window.L && window.tailwind) {
        setIsReady(true);
        clearInterval(checkScriptLoad);
      }
    }, 50);
    const timeout = setTimeout(() => setIsReady(true), 3000);
    return () => { clearInterval(checkScriptLoad); clearTimeout(timeout); };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      // Force refresh user to get latest emailVerified status
      if (u) u.reload().then(() => setUser({...auth.currentUser}));
      else setUser(null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'finland_pins'));
    return onSnapshot(q, (s) => {
      setPins(s.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // Map Init
  useEffect(() => {
    if (!isReady || !mapContainerRef.current || !window.L || mapInstanceRef.current) return;

    const L = window.L;
    const map = L.map(mapContainerRef.current, { zoomControl: false }).setView([64.9146659, 26.0672554], 5);
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    map.on('click', (e) => {
      window.dispatchEvent(new CustomEvent('map-click', { detail: e.latlng }));
    });

    mapInstanceRef.current = map;
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isReady]);

  // Markers
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;
    const map = mapInstanceRef.current;
    const L = window.L;

    const earthIcon = L.divIcon({
      className: 'bg-transparent',
      iconSize: [30, 42],
      iconAnchor: [15, 42],
      html: `
        <div class="relative w-full h-full drop-shadow-md hover:scale-110 transition-transform">
           <svg viewBox="0 0 24 24" fill="#515a47" stroke="#ffffff" stroke-width="1.5">
             <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
             <circle cx="12" cy="10" r="3" fill="#d7be82" stroke="none" />
           </svg>
        </div>`
    });

    const selectedIcon = L.divIcon({
      className: 'bg-transparent',
      iconSize: [36, 48],
      iconAnchor: [18, 48],
      html: `
        <div class="relative w-full h-full drop-shadow-xl animate-bounce">
           <svg viewBox="0 0 24 24" fill="#7a4419" stroke="#ffffff" stroke-width="2">
             <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
             <circle cx="12" cy="10" r="3" fill="#ffffff" stroke="none" />
           </svg>
        </div>`
    });

    Object.keys(markersRef.current).forEach(id => {
      if (!pins.find(p => p.id === id)) {
        map.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
      }
    });

    pins.forEach(pin => {
      const isSelected = selectedPin && selectedPin.id === pin.id;
      if (markersRef.current[pin.id]) {
        markersRef.current[pin.id].setIcon(isSelected ? selectedIcon : earthIcon);
        markersRef.current[pin.id].setZIndexOffset(isSelected ? 1000 : 0);
      } else {
        const marker = L.marker([pin.lat, pin.lng], { icon: earthIcon })
          .addTo(map)
          .on('click', () => {
            setSelectedPin(pin);
            setIsAddingMode(false);
          });
        markersRef.current[pin.id] = marker;
      }
    });
  }, [pins, isReady, selectedPin]);

  useEffect(() => {
    const handleMapClick = (e) => {
      if (isAddingMode) {
        setNewPinCoords(e.detail);
        setIsPinFormOpen(true);
        setIsEditing(false);
        setFormData({ title: '', intro: '' });
      }
    };
    window.addEventListener('map-click', handleMapClick);
    return () => window.removeEventListener('map-click', handleMapClick);
  }, [isAddingMode]);

  const handleSavePin = async (e) => {
    e.preventDefault();
    if (!user) return;
    try {
      const collectionRef = collection(db, 'artifacts', appId, 'public', 'data', 'finland_pins');
      const pinData = {
        title: formData.title,
        intro: formData.intro,
        timestamp: new Date().toISOString(),
        userId: user.uid,
      };
      if (isEditing && selectedPin) {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'finland_pins', selectedPin.id), pinData);
        setSelectedPin({ ...selectedPin, ...pinData });
      } else {
        await addDoc(collectionRef, { ...pinData, lat: newPinCoords.lat, lng: newPinCoords.lng });
      }
      setIsPinFormOpen(false);
      setIsAddingMode(false);
    } catch (error) { console.error(error); }
  };

  const handleDeletePin = async (pinId) => {
    if (!window.confirm("Haluatko varmasti poistaa tämän?")) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'finland_pins', pinId));
      setSelectedPin(null);
    } catch (error) { console.error(error); }
  };

  // Add Button Click Handler (With Verification Check)
  const handleAddClick = () => {
    if(!user) {
      setIsAuthModalOpen(true);
    } else if (!user.emailVerified) {
      // Don't allow unverified users to add
      alert("Vahvista sähköpostiosoitteesi voidaksesi lisätä kohteita!");
    } else {
      setIsAddingMode(!isAddingMode);
      setSelectedPin(null);
    }
  };

  if (!isReady) {
    return (
      <div className="h-screen w-full bg-brand-cream flex flex-col items-center justify-center text-brand-moss gap-4">
        <Compass size={48} className="animate-spin text-brand-brown" />
        <p className="font-bold tracking-widest text-sm">LADATAAN KARTTAA...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full bg-brand-cream font-sans text-brand-moss overflow-hidden">
      
      {/* HEADER */}
      <header className="flex-none bg-white border-b border-brand-sand relative z-50">
        <div className="px-6 py-4 flex items-center justify-between">
          
          {/* Logo (Left) */}
          <div className="flex items-center gap-2 z-10">
             <img src="/logo_and_text.png" alt="Tilalta" className="h-8 w-auto object-contain" onError={(e)=>e.target.style.display='none'}/>
             <span className="md:hidden font-bold text-xl text-brand-brown">TILALTA</span>
          </div>

          {/* Intro Text (Center) */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:block whitespace-nowrap">
             <p className="text-brand-moss/80 font-medium italic text-sm">
               OSTA RUOKA SUORAAN ENSITUOTTAJALTA
             </p>
          </div>

          {/* Controls (Right) */}
          <div className="flex items-center gap-4 z-10">
             <div className="hidden md:flex items-center gap-2 bg-brand-sand/10 px-4 py-1.5 rounded-full border border-brand-sand/30">
                <MapPin size={14} className="text-brand-brown" />
                <span className="text-brand-moss font-bold text-sm">{pins.length} kohdetta</span>
             </div>
             
             {user ? (
               <div className="flex items-center gap-3">
                 <div className="text-right hidden md:block leading-tight">
                   <p className="text-[10px] text-brand-sand uppercase font-bold tracking-wider">Käyttäjä</p>
                   <p className="font-bold text-sm">{user.email.split('@')[0]}</p>
                 </div>
                 <Button variant="outline" onClick={() => auth.signOut()} className="!py-1.5 !px-3 !text-sm">
                   <LogOut size={14} />
                 </Button>
               </div>
             ) : (
               <Button variant="accent" onClick={() => setIsAuthModalOpen(true)} className="!py-1.5 !px-4 !text-sm">
                 <LogIn size={14} /> Kirjaudu
               </Button>
             )}
          </div>
        </div>

        {/* Verification Warning Banner */}
        {user && !user.emailVerified && (
          <div className="bg-yellow-100 border-t border-yellow-200 py-1 text-center text-xs text-yellow-800 font-medium flex items-center justify-center gap-2">
            <AlertTriangle size={12} />
            <span>Sähköpostia ei ole vahvistettu. Vahvista sähköposti lisätäksesi kohteita.</span>
            <button onClick={() => window.location.reload()} className="underline hover:text-yellow-950">
               Päivitä
            </button>
          </div>
        )}
      </header>

      {/* MAIN CONTENT */}
      <div className="flex-1 relative">
        <div ref={mapContainerRef} className="w-full h-full z-0 bg-slate-200"></div>

        {/* FLOATING ACTION BUTTON */}
        <div className="absolute bottom-8 right-8 z-[400] flex flex-col items-end gap-4">
           {isAddingMode && (
             <div className="bg-brand-moss text-white px-4 py-2 rounded-lg shadow-lg animate-bounce">
               <p className="font-bold text-sm">Valitse sijainti...</p>
             </div>
           )}
           <button
             onClick={handleAddClick}
             className={`
               w-16 h-16 rounded-full border-4 border-white shadow-xl transition-all duration-300 flex items-center justify-center
               ${isAddingMode 
                 ? 'bg-brand-brown text-white rotate-45' 
                 : 'bg-brand-moss text-white hover:bg-[#3f4637] hover:scale-105'
               }
               ${user && !user.emailVerified ? 'opacity-75' : ''}
             `}
           >
             <Plus size={32} />
           </button>
        </div>

        {/* PIN DETAIL CARD */}
        {selectedPin && !isPinFormOpen && (
          <div className="absolute top-6 left-6 right-6 md:right-auto md:w-[380px] z-[500] animate-in fade-in slide-in-from-left-4 duration-300">
            <Card className="bg-brand-cream border-brand-sand">
               <div className="h-2 bg-gradient-to-r from-brand-moss to-brand-brown"></div>
               <div className="p-5">
                 <div className="flex justify-between items-start mb-3">
                    <Badge>Kohde</Badge>
                    <button onClick={() => setSelectedPin(null)} className="text-brand-moss/50 hover:text-brand-brown">
                      <X size={20} />
                    </button>
                 </div>
                 <h2 className="text-2xl font-bold text-brand-moss mb-1">{selectedPin.title}</h2>
                 <div className="flex items-center gap-2 text-brand-moss/60 text-xs mb-4 uppercase tracking-wide font-bold">
                   <User size={12}/>
                   <span>{user && selectedPin.userId === user.uid ? 'Oma ilmoitus' : 'Yhteisön ilmoitus'}</span>
                 </div>
                 <div className="bg-white p-4 rounded-lg border border-brand-sand/30 shadow-inner mb-5">
                   <p className="text-brand-moss/80 leading-relaxed text-sm">
                     {selectedPin.intro || "Ei lisätietoja."}
                   </p>
                 </div>
                 <div className="flex flex-col gap-3">
                   <a 
                     href={`https://www.google.com/maps/dir/?api=1&destination=$${selectedPin.lat},${selectedPin.lng}`}
                     target="_blank" rel="noreferrer" className="block">
                     <Button variant="outline" className="w-full justify-center">
                       <Navigation size={16} /> Hae reitti
                     </Button>
                   </a>
                   {user && selectedPin.userId === user.uid && (
                     <div className="flex gap-3 pt-3 border-t border-brand-sand/20">
                       <Button variant="ghost" onClick={() => {setIsEditing(true); setIsPinFormOpen(true); setFormData({ title: selectedPin.title, intro: selectedPin.intro });}} className="flex-1 text-xs">
                         <Edit2 size={14}/> Muokkaa
                       </Button>
                       <Button variant="danger" onClick={() => handleDeletePin(selectedPin.id)} className="flex-1 bg-white hover:bg-red-50 text-xs">
                         <Trash2 size={14}/> Poista
                       </Button>
                     </div>
                   )}
                 </div>
               </div>
            </Card>
          </div>
        )}
      </div>

      {/* AUTH MODAL */}
      {isAuthModalOpen && (
        <div className="fixed inset-0 z-[1000] bg-brand-moss/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md animate-in zoom-in-95 duration-200">
            <Card className="relative overflow-hidden">
              <button onClick={() => setIsAuthModalOpen(false)} className="absolute top-4 right-4 p-2 text-brand-moss/50 hover:text-brand-brown">
                <X size={20}/>
              </button>
              <AuthForm onLoginSuccess={() => setIsAuthModalOpen(false)} />
            </Card>
          </div>
        </div>
      )}

      {/* PIN FORM MODAL */}
      {isPinFormOpen && (
        <div className="fixed inset-0 z-[1000] bg-brand-moss/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md animate-in zoom-in-95 duration-200">
            <Card>
              <div className="p-5 border-b border-brand-sand/30 bg-brand-sand/10 flex justify-between items-center">
                 <h3 className="font-bold text-lg text-brand-moss flex items-center gap-2">
                   <Edit2 size={18} className="text-brand-brown"/>
                   {isEditing ? "Muokkaa" : "Uusi kohde"}
                 </h3>
                 <button onClick={() => setIsPinFormOpen(false)} className="text-brand-moss/50 hover:text-brand-brown"><X size={20}/></button>
              </div>
              <form onSubmit={handleSavePin} className="p-6 space-y-5 bg-brand-cream">
                <div>
                  <label className="block text-xs font-bold text-brand-brown uppercase mb-1">Otsikko</label>
                  <input 
                    type="text" required 
                    className="w-full px-4 py-3 rounded-lg bg-white border-2 border-brand-sand/50 focus:border-brand-brown outline-none transition-colors"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Esim. kilo porkkanaa"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-brand-brown uppercase mb-1">Tietoja</label>
                  <textarea 
                    rows={4} required 
                    className="w-full px-4 py-3 rounded-lg bg-white border-2 border-brand-sand/50 focus:border-brand-brown outline-none transition-colors resize-none"
                    value={formData.intro}
                    onChange={(e) => setFormData({...formData, intro: e.target.value})}
                    placeholder="Kerro tuotteistasi ja suunnistusohjeita"
                  />
                </div>
                <div className="pt-2">
                  <Button type="submit" variant="primary" className="w-full py-3 text-lg shadow-earthy">
                    {isEditing ? 'Tallenna muutokset' : 'Lisää kohde'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}